"use client";

import * as React from "react";
import { addDays, format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { DateRange, isDateRange } from "react-day-picker";
import { cn } from "@/lib/utils";

import { FieldValues, Path, PathValue, UseFormSetValue } from "react-hook-form";
import dayjs from "dayjs";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";
import { Calendar, CalendarProps } from "./calendar";
interface DatePickerProps<T extends FieldValues> {
  mode?: "single" | "range" | undefined;
  dateName?: Path<T>;
  setValue?: UseFormSetValue<T>;
}
export const DatePicker = <T extends FieldValues>({
  className,
  dateName,
  setValue,
  mode = "single",
}: DatePickerProps<T> &
  React.HTMLAttributes<HTMLDivElement> &
  CalendarProps) => {
  const [date, setDate] = React.useState<Date | DateRange | undefined>(
    mode == "single"
      ? new Date()
      : {
          from: new Date(2022, 0, 20),
          to: addDays(new Date(2022, 0, 20), 20),
        }
  );
  React.useEffect(() => {
    setValue &&
      dateName &&
      setValue(
        dateName,
        dayjs(date as Date).toISOString() as PathValue<T, Path<T>>
      );
  }, [date]);
  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              `w-[300px] justify-start text-left font-normal h-10 ${
                !date && "text-muted-foreground"
              }`,
              className
            )}
          >
            <CalendarIcon />
            {mode == "single" ? (
              date instanceof Date && date ? (
                format(date, "dd/MM/yyyy")
              ) : (
                "choisir un jour"
              )
            ) : mode == "range" && isDateRange(date) && date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto bg-white p-0" align="start">
          <Calendar
            mode="single"
            selected={date instanceof Date ? date : new Date()}
            onSelect={(day) => setDate(day)}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};
